import { dataIdFromObject } from '@sketch/graphql-cache'

import {
  ProjectFragment,
  ProjectFragmentDoc,
  ProjectChangedSubscription,
  GetProjectDetailsQuery,
  GetProjectDetailsQueryVariables,
  GetProjectDetailsDocument,
} from '@sketch/gql-types'

import {
  deleteWorkspaceProject,
  restoreWorkspaceProject,
} from '@sketch/modules-common'
import {
  StorageItemTransferLocation,
  updateCacheAfterStorageItemTransferred,
} from '../../../storageItems/operations/useTransferStorageItems'
import { ApolloCache, ApolloClient } from '@apollo/client'
import { ErrorHandler } from '@sketch/tracing'

export interface ProjectCacheOperation {
  cache: ApolloCache<any>
  identifier: string
}

interface ProjectRestoredOperation extends ProjectCacheOperation {
  workspaceIdentifier: string
  archived: boolean
}

interface ProjectDeletedOperation extends ProjectCacheOperation {
  workspaceIdentifier: string
  projectData: ProjectFragment
}

export const handleProjectDeleted = (params: ProjectDeletedOperation) => {
  const { cache, identifier, workspaceIdentifier } = params

  deleteWorkspaceProject(cache, workspaceIdentifier, identifier)
}

export const handleProjectRestored = (params: ProjectRestoredOperation) => {
  const { cache, identifier, workspaceIdentifier, archived } = params

  restoreWorkspaceProject(cache, workspaceIdentifier, identifier, archived)
}

export const getCachedProject = (params: ProjectCacheOperation) => {
  const { cache, identifier } = params

  try {
    return (
      cache.readFragment<ProjectFragment>({
        fragment: ProjectFragmentDoc,
        fragmentName: 'Project',
        id: dataIdFromObject({
          __typename: 'Project',
          identifier,
        })!,
      }) || undefined
    )
  } catch {
    // This is a preventive way of returning an error to the UI
    // If this returns an error we simulate that the object does not exist
    return undefined
  }
}

export const queryProjectDetails = async (
  client: ApolloClient<object>,
  identifier: string
) => {
  try {
    return client.query<
      GetProjectDetailsQuery,
      GetProjectDetailsQueryVariables
    >({
      query: GetProjectDetailsDocument,
      variables: { projectIdentifier: identifier },
      fetchPolicy: 'network-only',
    })
  } catch {
    return null
  }
}

export const handleProjectTransferred = async (
  client: ApolloClient<object>,
  // eslint-disable-next-line @typescript-eslint/no-restricted-types
  data: Extract<
    ProjectChangedSubscription['projectChanged'],
    { __typename: 'ProjectTransferredEvent' }
  >
) => {
  // Query project details, this serves us two purposes:
  // 1. Update the cache values with the new project details
  // 2. Get the project object that we can use later in this function
  const projectQuery = await queryProjectDetails(
    client,
    data.project.identifier
  )
  const project = projectQuery?.data.project
  if (!project) {
    // There were issues fetching the project details
    // Abort updating the cache
    ErrorHandler.shouldNeverHappen(
      'Folder details not found after folder transfer'
    )
    return
  }

  const location: StorageItemTransferLocation = {
    source: {
      workspaceId: data.oldWorkspaceIdentifier!,
      projectId: data.oldParentProjectIdentifier || undefined,
    },
    destination: {
      workspaceId: project.workspace.identifier,
      projectId: project.parentProjectIdentifier || undefined,
    },
  }

  updateCacheAfterStorageItemTransferred({
    cache: client,
    item: project,
    location,
  })

  return { items: [project], location }
}
